import React, {useState} from 'react';

import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {Owner, SchemaCollaborator} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  TEST_DEFINITION_CREATE_ENDED_EVENT,
  TEST_DEFINITION_CREATE_STARTED_EVENT,
} from 'performance/processSampleTestsEvents';
import * as TestDefinitionDAO from 'sample-tests/network/TestDefinitionDAO';
import TestDefinitionCollaboratorsDescription from 'sample-tests/ui/test-definitions/TestDefinitionCollaboratorsDescription';
import TestDefinitionMetadataSettings from 'sample-tests/ui/test-definitions/TestDefinitionMetadataSettings';
import SchemaWithOrgPermissionsCreateFlow from 'schemas/SchemaWithOrgPermissionsCreateFlow';
import getTestProps from 'test/lib/cypress/getTestProps';
import {withLoadingScreen} from 'util/Promises';

import {SchemaPermissionsGraphQL} from '../../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';

export interface TestDefinitionCreateFlowProps {
  ownerChoices: Array<Owner>;
  schemaPermissions: SchemaPermissionsGraphQL | null;
  organizations: Array<OrganizationDefaultSchemaCollaboratorsConfiguration>;
}

function TestDefinitionCreateFlow(props: TestDefinitionCreateFlowProps): JSX.Element {
  const [ownerId, setOwnerId] = useState(props.ownerChoices[0]?.id);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [resultSchemaId, setResultSchemaId] = useState<string>('');

  async function createTestDefinition(collaborators: Array<SchemaCollaborator>): Promise<void> {
    logProfilerTimingEvent(TEST_DEFINITION_CREATE_STARTED_EVENT);

    await withLoadingScreen(
      (async () => {
        const response = await TestDefinitionDAO.createTestDefinition({
          name,
          collaborators,
          description,
          organizationId: ownerId,
          resultSchemaId,
        });

        if (!response.createdIds.length) {
          notifications.error('Failed to create test definition.');
          return;
        }

        BenchlingUtil.navigate(null, TestDefinitionDAO.getTestDefinitionURL(response.createdIds[0]));
        notifications.success('Successfully created test definition.');
      })(),
      'Creating test definition...'
    );
    logProfilerTimingEvent(TEST_DEFINITION_CREATE_ENDED_EVENT);
  }

  function renderMetadataSettings(handleNext: () => void): JSX.Element {
    return (
      <TestDefinitionMetadataSettings
        ownerId={ownerId}
        setOwnerId={setOwnerId}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        resultSchemaId={resultSchemaId}
        setResultSchemaId={setResultSchemaId}
        handleNext={handleNext}
        ownerChoices={props.ownerChoices}
      />
    );
  }

  // Test Definitions are technically not schemas, but we use the same flow and permissioning models
  return (
    <div {...getTestProps({component: 'TestDefinitionCreateFlow', element: 'root'})}>
      <SchemaWithOrgPermissionsCreateFlow
        createSchema={createTestDefinition}
        defaultSchemaCollaboratorsSchemaType={
          DefaultSchemaCollaboratorsConfigurationSchemaType.TEST_DEFINITION
        }
        metadataTitle="TestDefinition Settings"
        isSchemaPermissionsEnabled={true}
        ownerId={ownerId}
        organizations={props.organizations}
        renderMetadataSettings={renderMetadataSettings}
        schemaCollaboratorsDescription={<TestDefinitionCollaboratorsDescription />}
      />
    </div>
  );
}

export default TestDefinitionCreateFlow;
